import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Flex, Input, Modal, Typography } from 'antd'
import { CopyOutlined, LoadingOutlined, SyncOutlined } from '@ant-design/icons'
import { Container } from 'typescript-ioc/es5'
import { Well } from '@pushly/aqe/lib/components'
import { useService } from '@pushly/aqe/lib/hooks'
import { DomainWebhookService } from '../../services/domain-webhook.service'
import { simpleNotification } from '../../_utils/utils'
import { AppState } from '../../stores/app'
import dayjs from 'dayjs'

const { Text, Paragraph } = Typography

interface SigningSecretMetadata {
    exists: boolean
    created_at: string | null
    updated_at: string | null
    masked_value: string | null
}

type SecretState = 'loading' | 'empty' | 'masked' | 'revealed'

export const SigningVerificationSection = (props: { domainId: number; onSecretResolved?: (exists: boolean) => void }) => {
    const { domainId, onSecretResolved } = props
    const webhookService = useService(DomainWebhookService)
    const appState = Container.get(AppState)
    const domainTz = appState.currentDomain?.timezone ?? dayjs.tz.guess()

    const [secretState, setSecretState] = useState<SecretState>('loading')
    const [metadata, setMetadata] = useState<SigningSecretMetadata | null>(null)
    const revealedValueRef = useRef<string | null>(null)
    const [revealedValue, setRevealedValue] = useState<string | null>(null)
    const [actionLoading, setActionLoading] = useState(false)

    const fetchMetadata = useCallback(async () => {
        setSecretState('loading')
        const res = await webhookService.getSigningSecretMetadata(domainId)
        if (res.ok && res.data?.exists) {
            setMetadata(res.data)
            setSecretState('masked')
            onSecretResolved?.(true)
        } else {
            setMetadata(null)
            setSecretState('empty')
            onSecretResolved?.(false)
        }
    }, [domainId])

    useEffect(() => {
        fetchMetadata()
    }, [fetchMetadata])

    const handleRevealAndCopy = async () => {
        setActionLoading(true)
        try {
            let value = revealedValueRef.current
            if (!value) {
                const res = await webhookService.revealSigningSecret(domainId)
                if (res.ok && res.data?.value) {
                    value = res.data.value
                    revealedValueRef.current = value
                    setRevealedValue(value)
                    setSecretState('revealed')
                } else {
                    simpleNotification('error', 'Failed to reveal signing secret.')
                    return
                }
            }
            await navigator.clipboard.writeText(value!)
            simpleNotification('success', 'Signing secret copied to clipboard.')
        } catch {
            simpleNotification('error', 'Failed to copy to clipboard.')
        } finally {
            setActionLoading(false)
        }
    }

    const handleRegenerate = () => {
        Modal.confirm({
            title: 'Regenerate Signing Secret',
            width: 520,
            content: (
                <Text>
                    This will invalidate the current signing secret immediately. Any webhook receivers
                    using the old secret will fail signature verification until updated with the new
                    value. In-flight webhook deliveries may also fail during the transition.
                </Text>
            ),
            okText: 'Regenerate',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: async () => {
                const res = await webhookService.regenerateSigningSecret(domainId)
                if (res.ok && res.data?.value) {
                    revealedValueRef.current = res.data.value
                    setRevealedValue(res.data.value)
                    setSecretState('revealed')
                    simpleNotification('success', 'Signing secret regenerated.')
                } else {
                    simpleNotification('error', 'Failed to regenerate signing secret.')
                }
            },
        })
    }

    const formatTimestamp = (iso: string) => {
        const dt = dayjs(iso).tz(domainTz)
        return `${dt.format('MMM D, YYYY h:mm A')} ${dt.format('z')}`
    }

    const isRevealed = secretState === 'revealed'

    const renderHeaderActions = () => {
        if (secretState !== 'masked' && secretState !== 'revealed') return null
        return (
            <Button
                size="small"
                icon={<SyncOutlined />}
                style={{ color: '#d97706', borderColor: '#d97706' }}
                onClick={handleRegenerate}
            >
                Regenerate
            </Button>
        )
    }

    const renderBody = () => {
        if (secretState === 'empty') {
            return (
                <Alert
                    type="info"
                    showIcon
                    message="A signing secret will be generated when you create your first webhook."
                />
            )
        }

        const displayValue = isRevealed ? revealedValue ?? '' : metadata?.masked_value ?? ''

        return (
            <Flex vertical gap={16}>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                    Webhooks delivered by Pushly include an <Text code>X-Pushly-Signature</Text> header
                    signed with this secret. Use the secret in your webhook endpoint to verify each
                    request is authentic.
                </Paragraph>
                <Input
                    readOnly
                    value={displayValue}
                    style={{
                        fontFamily: 'monospace',
                        fontSize: 14,
                        maxWidth: 560,
                        cursor: 'default',
                        caretColor: 'transparent',
                    }}
                    styles={{
                        input: {
                            backgroundColor: '#f5f5f5',
                            color: 'var(--ant-color-text)',
                        },
                    }}
                    addonAfter={
                        <span
                            onClick={handleRevealAndCopy}
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0 4px',
                                color: 'var(--ant-color-text)',
                            }}
                            role="button"
                            aria-label="Copy signing secret"
                        >
                            {actionLoading
                                ? <LoadingOutlined style={{ fontSize: 16, color: 'var(--ant-color-primary)' }} spin />
                                : <CopyOutlined style={{ fontSize: 16 }} />
                            }
                        </span>
                    }
                />
                {metadata?.updated_at && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Active since {formatTimestamp(metadata.updated_at)}
                    </Text>
                )}
            </Flex>
        )
    }

    return (
        <Well
            title="Signing Secret"
            titleTransform="none"
            actions={renderHeaderActions}
            hideFooter={true}
            loading={secretState === 'loading' && {
                enabled: true,
                icon: <LoadingOutlined spin style={{ fontSize: 24, color: 'var(--ant-color-primary)' }} />,
            }}
        >
            {renderBody()}
        </Well>
    )
}
